import React, { useState, useMemo, useEffect, useContext } from 'react'
import { groupBy, orderBy } from 'lodash'
import { useCharacterWishlist } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import PersonalAgeModal from './PersonalAgeModal'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import PersonalWish from './PersonalWish'
import { LootContext, PersonalLootContext } from '../context'
import { useFindRelevantWish } from '../helpers'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import { Skeleton } from '@/components/ui/skeleton'
import { EditableWish } from '../types'
import { DialogClose, DialogFooter } from '@/components/ui/dialog'
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import DifficultySelector from './DifficultySelector'
import Card from 'app/frontend/base/components/Card'

const PersonalWishlist = () => {
  const { difficulty, selectedSeason, configuration } = useContext(LootContext)
  const { characterReference, changes, setChanges } =
    useContext(PersonalLootContext)
  const [editingComment, setEditingComment] = useState<
    [wowaudit.WishlistItem, EditableWish] | undefined
  >(undefined)
  const [currentComment, setCurrentComment] = useState('')
  const [view, setView] = useState<keyof wowaudit.CharacterWishlist['order']>(
    (localStorage.getItem(
      'wishlistPersonalView',
    ) as keyof wowaudit.CharacterWishlist['order']) || 'display_slot',
  )

  const defaultSpecId =
    characterReference.currentSpec?.id ??
    parseInt(
      Object.keys(getSpecializationsByClass(characterReference.classId))[0],
    )
  const [currentSpecId, setCurrentSpecId] = useState(defaultSpecId)

  const findRelevantWish = useFindRelevantWish(currentSpecId)

  const { team } = useTeam()
  const { data: wishlist, isPending: loading } = useCharacterWishlist(
    team.id,
    characterReference.id,
    selectedSeason.id,
  )

  const groupedWishlist = useMemo(() => {
    if (currentSpecId && wishlist) {
      return groupBy(
        orderBy(
          wishlist.items.filter((item) => item.specs.includes(currentSpecId)),
          [(i) => findRelevantWish(i)?.percentage || 0],
          ['desc'],
        ),
        view,
      )
    }
  }, [wishlist, currentSpecId, view])

  useEffect(() => {
    if (wishlist?.specs) {
      setCurrentSpecId(
        wishlist.specs.find((spec) => spec.default)?.id || defaultSpecId,
      )
    }
  }, [wishlist])

  if (loading) {
    return <Skeleton className='h-160 mt-24' />
  } else {
    return (
      <div className='mt-6'>
        <div className='flex items-end gap-2'>
          <DifficultySelector className='grow' />
          {wishlist?.specs && (
            <div className='flex flex-col gap-1'>
              <span className='font-semibold text-right'>Specialization</span>

              <ul className='flex gap-1'>
                {wishlist.specs.map((spec) => {
                  return (
                    <li
                      key={spec.id}
                      className={`cursor-pointer flex items-end`}
                      onClick={() => {
                        setCurrentSpecId(spec.id)
                      }}
                    >
                      <Tooltip delayDuration={500}>
                        <TooltipTrigger>
                          <BlizzardIcon
                            className={
                              spec.id === currentSpecId
                                ? 'border-primary'
                                : 'grayscale border-transparent'
                            }
                            iconName={spec.icon}
                            size='large'
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          side='bottom'
                          className='flex items-center gap-2'
                        >
                          <CharacterRoleIcon role={spec.role} />
                          {spec.name}
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>

        <Card className='p-5 mt-2'>
          <div className='flex justify-between items-center'>
            <div className='justify-center flex gap-2 grow pl-6 uppercase font-medium'>
              <span className='opacity-70'>View by</span>{' '}
              <span
                onClick={() => {
                  setView('display_slot')
                  localStorage.setItem('wishlistPersonalView', 'display_slot')
                }}
                className={`cursor-pointer transition-opacity ${
                  view === 'display_slot'
                    ? 'text-foreground'
                    : 'opacity-30 hover:opacity-100'
                }`}
              >
                Item slot
              </span>
              <span className='opacity-70'>/</span>
              <span
                onClick={() => {
                  setView('encounter_name')
                  localStorage.setItem('wishlistPersonalView', 'encounter_name')
                }}
                className={`cursor-pointer transition-opacity ${
                  view === 'encounter_name'
                    ? 'text-foreground'
                    : 'opacity-30 hover:opacity-100'
                }`}
              >
                Encounter
              </span>
            </div>
            <PersonalAgeModal wishlist={wishlist} />
            <Dialog
              open={editingComment !== undefined}
              onOpenChange={() => {
                setEditingComment(undefined)
                setCurrentComment('')
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit comment</DialogTitle>
                </DialogHeader>

                <Input
                  placeholder='Comment'
                  value={currentComment}
                  onChange={(e) => setCurrentComment(e.target.value)}
                />

                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant='secondary'>Cancel</Button>
                  </DialogClose>
                  <Button
                    onClick={() => {
                      const wishId = `${editingComment?.[0].id}-${difficulty}-${currentSpecId}-${configuration.id}`
                      setChanges(
                        Object.assign({}, changes, {
                          [wishId]: Object.assign(
                            {},
                            editingComment?.[1] || { percentage: 0 },
                            { comment: currentComment },
                          ),
                        }),
                      )
                      setEditingComment(undefined)
                      setCurrentComment('')
                    }}
                  >
                    OK
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {groupedWishlist && wishlist
            ? wishlist.order[view].map((category) => {
                if (groupedWishlist[category as string]) {
                  return (
                    <div className='category' key={category as string}>
                      <div className='uppercase font-medium border-b'>
                        {category.replaceAll('_', ' ')}
                      </div>

                      <div className='grid grid-cols-1 min-[720px]:grid-cols-2 lg:grid-cols-3 min-[1350px]:grid-cols-4 gap-2 mt-2 mb-5'>
                        {groupedWishlist[category as string].map((item) => {
                          return (
                            <PersonalWish
                              key={item.id}
                              item={item}
                              view={view}
                              currentSpecId={currentSpecId}
                              setEditingComment={([item, wish]) => {
                                setCurrentComment(wish.comment || '')
                                setEditingComment([item, wish])
                              }}
                            />
                          )
                        })}
                      </div>
                    </div>
                  )
                }
              })
            : null}
        </Card>
      </div>
    )
  }
}

export default PersonalWishlist
